import { useState } from 'react';
import type { MockAuthState, KontentinoPage } from '../types/schedule';

// Mock page data that simulates OAuth response
const MOCK_PAGES: KontentinoPage[] = [
  // Instagram pages
  {
    id: 'ig-brand-main',
    platform: 'instagram',
    name: 'My Brand Instagram',
    username: 'mybrand',
    avatar: 'https://api.dicebear.com/7.x/initials/svg?seed=MB&backgroundColor=c026d3',
    isDefault: true,
  },
  {
    id: 'ig-personal',
    platform: 'instagram',
    name: 'Personal Account',
    username: 'filip.papranec',
    avatar: 'https://api.dicebear.com/7.x/initials/svg?seed=FP&backgroundColor=2563eb',
  },
  {
    id: 'ig-client-xyz',
    platform: 'instagram',
    name: 'Client XYZ Instagram',
    username: 'clientxyz',
    avatar: 'https://api.dicebear.com/7.x/initials/svg?seed=CX&backgroundColor=16a34a',
  },

  // LinkedIn pages
  {
    id: 'li-company',
    platform: 'linkedin',
    name: 'Kontentino Company Page',
    username: 'kontentino',
    avatar: 'https://api.dicebear.com/7.x/initials/svg?seed=K&backgroundColor=0a66c2',
    isDefault: true,
  },
  {
    id: 'li-personal',
    platform: 'linkedin',
    name: 'Filip Papranec',
    username: 'filipapranec',
    avatar: 'https://api.dicebear.com/7.x/initials/svg?seed=FP&backgroundColor=0a66c2',
  },

  // Facebook pages
  {
    id: 'fb-brand',
    platform: 'facebook',
    name: 'My Brand Page',
    username: 'mybrandpage',
    avatar: 'https://api.dicebear.com/7.x/initials/svg?seed=MB&backgroundColor=1877f2',
    isDefault: true,
  },
  {
    id: 'fb-client-abc',
    platform: 'facebook',
    name: 'Client ABC Facebook',
    username: 'clientabc',
    avatar: 'https://api.dicebear.com/7.x/initials/svg?seed=CA&backgroundColor=1877f2',
  },

  // Twitter/X pages
  {
    id: 'tw-brand',
    platform: 'twitter',
    name: 'My Brand',
    username: 'mybrand',
    avatar: 'https://api.dicebear.com/7.x/initials/svg?seed=MB&backgroundColor=000000',
    isDefault: true,
  },

  // TikTok pages
  {
    id: 'tt-brand',
    platform: 'tiktok',
    name: 'My Brand TikTok',
    username: 'mybrand',
    avatar: 'https://api.dicebear.com/7.x/initials/svg?seed=MB&backgroundColor=000000',
    isDefault: true,
  },

  // YouTube pages
  {
    id: 'yt-brand',
    platform: 'youtube',
    name: 'My Brand Channel',
    username: 'mybrandchannel',
    avatar: 'https://api.dicebear.com/7.x/initials/svg?seed=MB&backgroundColor=ff0000',
    isDefault: true,
  },

  // Pinterest pages
  {
    id: 'pi-brand',
    platform: 'pinterest',
    name: 'My Brand Pinterest',
    username: 'mybrand',
    avatar: 'https://api.dicebear.com/7.x/initials/svg?seed=MB&backgroundColor=e60023',
    isDefault: true,
  },

  // Threads pages
  {
    id: 'th-brand',
    platform: 'threads',
    name: 'My Brand Threads',
    username: 'mybrand',
    avatar: 'https://api.dicebear.com/7.x/initials/svg?seed=MB&backgroundColor=000000',
    isDefault: true,
  },

  // Google My Business
  {
    id: 'gmb-brand',
    platform: 'google_my_business',
    name: 'My Brand Business',
    username: 'my-brand',
    avatar: 'https://api.dicebear.com/7.x/initials/svg?seed=MB&backgroundColor=4285f4',
    isDefault: true,
  },
];

/**
 * Mock authentication hook that simulates Kontentino OAuth
 *
 * In production, this will be replaced with real OAuth:
 * - Login flow opens OAuth popup/page
 * - Pages fetched from Kontentino API
 * - Tokens stored securely (not in widget state)
 *
 * For now, returns mock data to build UI
 */
export function useMockAuth(): MockAuthState {
  // TODO: Replace with real OAuth when backend is ready
  // This hook should match the interface of useKontentinoAuth()

  const [authState] = useState<MockAuthState>({
    isAuthenticated: true, // Mock: always authenticated for testing
    userName: 'Filip Papranec',
    userId: 'mock-user-123',
    pages: MOCK_PAGES,
  });

  return authState;
}

/**
 * Helper: Get pages for a specific platform
 */
export function usePagesForPlatform(platform: string): KontentinoPage[] {
  const auth = useMockAuth();
  return auth.pages.filter((page) => page.platform === platform);
}

/**
 * Helper: Get default page for platform
 */
export function useDefaultPage(platform: string): KontentinoPage | undefined {
  const auth = useMockAuth();
  return auth.pages.find((page) => page.platform === platform && page.isDefault);
}
